<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRevendasVitrineStore } from '@/stores/revendasVitrine'
import { useApi } from '@/utils/axios'
import { REVENDA_METRICAS_ROTAS } from '@/utils/apiRoutes'

const store = useRevendasVitrineStore()

// filtros
const dataInicio = ref('')
const dataFim = ref('')
const idRevenda = ref(null)
const idRevendedor = ref(null)

// autocomplete options
const revendasOptions = ref([])
const revendedoresOptions = ref([])
const loadingRevendas = ref(false)
const loadingRevendedores = ref(false)

let revendasDebounce = null
let revendedoresDebounce = null

async function searchRevendas(query) {
  clearTimeout(revendasDebounce)
  revendasDebounce = setTimeout(async () => {
    loadingRevendas.value = true
    try {
      const res = await useApi(REVENDA_METRICAS_ROTAS.REVENDAS).fetch({
        params: { name: query || undefined, per_page: 15 },
      })
      revendasOptions.value = res?.data?.data ?? []
    } catch {
      revendasOptions.value = []
    } finally {
      loadingRevendas.value = false
    }
  }, 350)
}

async function searchRevendedores(query) {
  clearTimeout(revendedoresDebounce)
  revendedoresDebounce = setTimeout(async () => {
    loadingRevendedores.value = true
    try {
      const res = await useApi(REVENDA_METRICAS_ROTAS.REVENDEDORES).fetch({
        params: { name: query || undefined, per_page: 15 },
      })
      revendedoresOptions.value = res?.data?.data ?? []
    } catch {
      revendedoresOptions.value = []
    } finally {
      loadingRevendedores.value = false
    }
  }, 350)
}
const gasDoPovoFilter = ref(false)
const revendaRapidaFilter = ref(false)
const perPage = ref(10)
const page = ref(1)

// formata datetime-local (YYYY-MM-DDTHH:MM) para Y-m-d H:i:s (Laravel)
function formatDateTimeForApi(value) {
  if (!value) return undefined
  return value.replace('T', ' ') + ':00'
}

// debounce simples para filtros
let debounceTimer = null
function debounceFetch(delay = 400) {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    fetchList()
  }, delay)
}

async function fetchList() {
  await store.fetchMetricas({
    data_inicio: formatDateTimeForApi(dataInicio.value),
    data_fim: formatDateTimeForApi(dataFim.value),
    idrevenda: idRevenda.value || undefined,
    idrevendedor: idRevendedor.value || undefined,
    gas_do_povo: gasDoPovoFilter.value || undefined,
    revenda_rapida: revendaRapidaFilter.value || undefined,
    per_page: perPage.value,
    page: page.value,
  })
}

function resetFilters() {
  dataInicio.value = ''
  dataFim.value = ''
  idRevenda.value = null
  idRevendedor.value = null
  revendasOptions.value = []
  revendedoresOptions.value = []
  gasDoPovoFilter.value = false
  revendaRapidaFilter.value = false
  page.value = 1
  store.resetFilters()
}

// watchers
watch([perPage, page], () => {
  fetchList()
})

watch([dataInicio, dataFim, idRevenda, idRevendedor, gasDoPovoFilter, revendaRapidaFilter], () => {
  page.value = 1
  debounceFetch()
})

onMounted(() => {
  const now = new Date()
  const dateStr = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    .toISOString()
    .substr(0, 10)
  dataInicio.value = `${dateStr}T00:00`
  dataFim.value = `${dateStr}T23:59`
  searchRevendas()
  searchRevendedores()
  fetchList()
})

// computed
const items = computed(() => store.items || [])
const loading = computed(() => store.loading)
const meta = computed(() => store.meta || { current_page: 1, last_page: 1, total: 0 })

const totalAparicoes = computed(() =>
  items.value.reduce((acc, item) => acc + (item.qtd_aparicoes || 0), 0),
)

const posicaoMediaGeral = computed(() => {
  if (!items.value.length) return '-'
  const media =
    items.value.reduce((acc, item) => acc + parseFloat(item.posicao_media || 0), 0) /
    items.value.length
  return media.toFixed(2)
})
</script>

<template>
  <div>
    <v-row class="mb-3">
      <v-col cols="12" class="d-flex align-center">
        <h1 class="text-h4">Revendas na Vitrine</h1>
        <v-spacer></v-spacer>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card class="pa-3 mb-3">
      <v-row align="center">
        <v-col cols="12" md="3">
          <v-text-field
            v-model="dataInicio"
            type="datetime-local"
            label="Data início"
            density="compact"
            hide-details
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            v-model="dataFim"
            type="datetime-local"
            label="Data fim"
            density="compact"
            hide-details
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="idRevenda"
            :items="revendasOptions"
            :loading="loadingRevendas"
            item-title="nomefantasia"
            item-value="id"
            label="Revenda"
            density="compact"
            hide-details
            clearable
            no-filter
            @update:search="searchRevendas"
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="idRevendedor"
            :items="revendedoresOptions"
            :loading="loadingRevendedores"
            item-title="razaosocial"
            item-value="id"
            label="Revendedor"
            density="compact"
            hide-details
            clearable
            no-filter
            @update:search="searchRevendedores"
          />
        </v-col>
      </v-row>

      <v-row align="center" class="mt-1">
        <!-- COMENTADO POIS ESSAS INFORMACOES AINDA NAO ESTAO SENDO SALVAS NO HISTORICO
        <v-col cols="12" md="2" class="d-flex align-center">
          <v-switch
            v-model="gasDoPovoFilter"
            label="Gás do Povo"
            density="compact"
            hide-details
            color="primary"
          />
        </v-col>

        <v-col cols="12" md="2" class="d-flex align-center">
          <v-switch
            v-model="revendaRapidaFilter"
            label="Revenda Rápida"
            density="compact"
            hide-details
            color="primary"
          />
        </v-col> -->

        <v-col cols="12" md="8" class="d-flex align-end justify-end ga-2">
          <v-btn @click="resetFilters">Limpar</v-btn>
          <v-btn color="primary" @click="fetchList">Aplicar filtros</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Cards de resumo -->
    <v-row class="mb-3">
      <v-col cols="12" md="6">
        <v-card class="summary-card summary-card--primary pa-4" rounded="lg" elevation="2">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-medium-emphasis mb-1">Total de Aparições</div>
              <div class="text-h3 font-weight-black">{{ totalAparicoes }}</div>
              <div class="text-caption text-medium-emphasis mt-1">aparições na página atual</div>
            </div>
            <v-icon size="72" color="primary" opacity="0.2" class="summary-icon"
              >mdi-eye-outline</v-icon
            >
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="summary-card summary-card--warning pa-4" rounded="lg" elevation="2">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-medium-emphasis mb-1">Posição Média Geral</div>
              <div class="text-h3 font-weight-black">{{ posicaoMediaGeral }}</div>
              <div class="text-caption text-medium-emphasis mt-1">posição média na vitrine</div>
            </div>
            <v-icon size="72" color="warning" opacity="0.2" class="summary-icon">mdi-podium</v-icon>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="store.error" type="error" class="mb-4">{{ store.error }}</v-alert>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-2"
    ></v-progress-linear>

    <v-data-table
      :headers="[
        { title: 'Revenda', value: 'idrevenda' },
        { title: 'Marca', value: 'marca' },
        { title: 'Posição Média', value: 'posicao_media' },
        { title: 'Qtd. Aparições', value: 'qtd_aparicoes' },
        { title: 'Tipo Produto', value: 'tipo_produto' },
        { title: 'Peso (kg)', value: 'peso' },
      ]"
      :items="items"
      class="custom-table"
      :items-per-page="perPage"
    >
      <template #item="{ item }">
        <tr>
          <td>{{ item.nomefantasia || item.idrevenda }}</td>
          <td>{{ item.marca || '-' }}</td>
          <td>{{ item.posicao_media ? parseFloat(item.posicao_media).toFixed(2) : '-' }}</td>
          <td>{{ item.qtd_aparicoes ?? '-' }}</td>
          <td>{{ item.tipo_produto ?? '-' }}</td>
          <td>{{ item.peso != null ? item.peso : '-' }}</td>
        </tr>
      </template>

      <template #bottom>
        <div class="d-flex justify-space-between align-center px-4 py-2">
          <div class="d-flex align-center">
            <span class="text-caption text-secondary mr-4">
              Total de {{ meta.total || items.length }} item{{ (meta.total || 0) !== 1 ? 's' : '' }}
            </span>

            <v-select
              v-model="perPage"
              :items="[10, 25, 50, 100]"
              label="Itens por página"
              variant="outlined"
              dense
              hide-details
              style="width: 140px"
            />
          </div>

          <v-pagination
            v-if="meta.last_page > 1"
            :model-value="page"
            :length="meta.last_page"
            :total-visible="5"
            @update:model-value="(newPage) => (page = newPage)"
            rounded
            class="my-2"
          ></v-pagination>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>
.summary-card {
  position: relative;
  overflow: hidden;
  border-left: 4px solid;
}

.summary-card--primary {
  border-color: rgb(var(--v-theme-primary));
}

.summary-card--warning {
  border-color: rgb(var(--v-theme-warning));
}

.summary-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.text-h3 {
  line-height: 1.1;
}
</style>
